/**
 * PD-16873 — "visible only" client rows.
 *
 * A tenant may let sales reps SEE accounts they do not own in client lists and
 * searches (the list's "Assigned accounts only" setting off), so a rep knows an
 * account exists and who owns it. Seeing is not opening: whether a row may be
 * opened is decided by the same OPEN-level rule as the client detail page
 * (crm-api `VisibilityScopeService.resolveOpenScope`). Every client list and
 * search response therefore marks each row `access: 'open'` or
 * `access: 'restricted'`, and a restricted row carries the directory fields
 * below and nothing else — the server removes the rest, so no screen or agent
 * tool can leak revenue, balances, activity or contacts for an account the
 * caller cannot open.
 *
 * Isomorphic on purpose: the server redacts with these helpers and the screens
 * read the flag with them, so the contract is written down once.
 */

export const CLIENT_ROW_ACCESS = {
	OPEN: 'open',
	RESTRICTED: 'restricted',
} as const;

export type ClientRowAccess =
	(typeof CLIENT_ROW_ACCESS)[keyof typeof CLIENT_ROW_ACCESS];

/** The fields a list or search row gains from the row-access decision. */
export interface ClientRowAccessFieldsModel {
	access: ClientRowAccess;
	/**
	 * The primary salesperson's display name, set on RESTRICTED rows only so a
	 * screen or the agent can say whose account it is. Null when the account
	 * has no primary salesperson.
	 */
	ownerName?: string | null;
}

/**
 * The directory fields a restricted row keeps, by response property name.
 *
 * Owner decision: name (and DBA / legal name), street address, city, state,
 * zip, phone, business type, customer-vs-lead status, owner name, plus the ids
 * a screen needs to render the row. `externalId` / `externalId2` count as such
 * ids: the client list's number badge shows `externalId2 ?? externalId ?? id`,
 * and the client pickers already show that number to anyone who may list the
 * account. Coordinates and the distance are derived from the street address
 * and let a visible-only account appear on the map. Everything else —
 * revenue, gross profit, balances, credit, forecasts, activity dates, email,
 * contacts, notes, pricing, custom fields — is dropped.
 *
 * An allow-list rather than a deny-list so a column added to the client list
 * later is hidden from restricted rows until someone decides otherwise.
 */
export const CLIENT_DIRECTORY_FIELDS: ReadonlyArray<string> = [
	'id',
	'_id',
	'name',
	'dba',
	'legalName',
	'address1',
	'address2',
	'city',
	'state',
	'zip',
	'county',
	'country',
	'phone',
	'extension',
	'businessType',
	'leadOrCustomer',
	'externalId',
	'externalId2',
	'fkUserId',
	'salespersonName',
	'latitude',
	'longitude',
	'proximity',
	'relevance',
	'access',
	'ownerName',
];

const UNSAFE_KEYS: ReadonlySet<string> = new Set([
	'__proto__',
	'constructor',
	'prototype',
]);

/**
 * A copy of `row` holding only the `keep` keys (the directory fields by
 * default). Keys absent from the row stay absent rather than appearing as
 * null, so a screen renders exactly what it renders for any missing value.
 */
export function redactToClientDirectory<T extends Record<string, unknown>>(
	row: T,
	keep: ReadonlyArray<string> = CLIENT_DIRECTORY_FIELDS,
): Partial<T> {
	const redacted: Record<string, unknown> = {};

	for (const key of keep) {
		// Assigning `__proto__` would swap the copy's prototype for whatever the
		// row held under that key, and its keys would then read through.
		if (UNSAFE_KEYS.has(key)) {
			continue;
		}

		if (Object.prototype.hasOwnProperty.call(row, key)) {
			redacted[key] = row[key];
		}
	}

	return redacted as Partial<T>;
}

/** Whether a list or search row was marked visible-only by the server. */
export function isRestrictedClientRow(row: unknown): boolean {
	return (
		row != null &&
		typeof row === 'object' &&
		(row as { access?: unknown }).access === CLIENT_ROW_ACCESS.RESTRICTED
	);
}

/**
 * The owner's display name on a restricted row, or null when the row is not
 * restricted, the account is unassigned, or the name is blank.
 */
export function getRestrictedClientOwnerName(row: unknown): string | null {
	if (!isRestrictedClientRow(row)) {
		return null;
	}

	const ownerName = (row as { ownerName?: unknown }).ownerName;

	if (typeof ownerName !== 'string') {
		return null;
	}

	const trimmed = ownerName.trim();

	return trimmed === '' ? null : trimmed;
}
